import { shipmentReference } from "@/lib/shipment-reference";
import { taskInstructions } from "@/features/dispatch/selectors";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Loader2,
  Maximize2,
  Package,
  PauseCircle,
  Play,
  Snowflake,
  StickyNote,
  AlertTriangle,
  Undo2,
  X,
  XCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  type Task,
  TaskStatus,
  type TaskType,
  type Trip,
} from "@/api.types.v2";
import {
  useWaypointById,
  useStartWaypoint,
  useSplitWaypoint,
} from "@/api/waypoints";
import { useTripWaypoints } from "@/api/trips";
import { useCompleteTask, useFailTask } from "@/api/tasks";
import { getTaskTypeDisplay } from "@/features/tasks/tasks.helper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getOperatorDisplayName, getOperatorInitials } from "@/lib/safe-access";
import { taskLane, LANE_META } from "./task-type-meta";
import { formatEta, isTaskDone, type DerivedStop } from "./trip-stops";
import { ShipmentStatusBadge } from "./atoms";
import { shipmentDisplayStatus } from "./status-meta";
import { taskIsOnCancellationHold } from "./work-hold";
import { taskAddressLine, taskRoutingPin } from "./task-labels";

interface WaypointDetailProps {
  trip: Trip;
  stop: DerivedStop;
  onClose: () => void;
  onOpenTrip: (trip: Trip) => void;
}

// directional type badge (rounded square + arrow), like the assign rows
function DirBadge({ type }: { type: TaskType }) {
  const lane = taskLane(type);
  const cls = "h-[13px] w-[13px]";
  const icon =
    lane === "pickup" ? (
      <ArrowUp className={cls} strokeWidth={2.6} />
    ) : lane === "return" ? (
      <Undo2 className={cls} strokeWidth={2.6} />
    ) : (
      <ArrowDown className={cls} strokeWidth={2.6} />
    );
  return (
    <Badge
      className="size-[22px] shrink-0 rounded-md border-0 p-0 text-white [&>svg]:size-[13px]"
      style={{ background: LANE_META[lane].color }}
    >
      {icon}
    </Badge>
  );
}

// denormalized task fields present on the waypoint-by-id response
type RichTask = Task & {
  addressInstructions?: string;
  proof?: { mediaItems?: { url: string }[] };
};

function TaskCard({
  task,
  onComplete,
  onFail,
  busy,
}: {
  task: RichTask;
  onComplete: () => void;
  onFail: () => void;
  busy: boolean;
}) {
  const ship = task.shipment;
  const sNum = (ship as { shipmentNumber?: number } | undefined)
    ?.shipmentNumber;
  const ord = ship?.order?.orderNumber;
  const pkgs = ship?.packages ?? [];
  const weight = pkgs.reduce((n, p) => n + (p.weightGrams ?? 0), 0);
  const fragile = pkgs.some((p) => p.isFragile);
  const cold = pkgs.some((p) => p.requiresCooling);
  const status = ship ? shipmentDisplayStatus(ship) : undefined;
  const note = taskInstructions(task);
  const unit = (task.addressLine2 ?? task.address?.line2)?.trim();
  const inProgress = task.status === TaskStatus.IN_PROGRESS;
  const done = task.status === TaskStatus.COMPLETED;
  const failed = task.status === TaskStatus.FAILED;
  const held = taskIsOnCancellationHold(task);
  const proof = task.proof?.mediaItems;

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white ${held ? "border-zinc-300 opacity-75 grayscale" : "border-zinc-200"}`}
    >
      <div className="px-3 py-2.5">
        <div className="break-words font-mono text-sm font-semibold">
          {shipmentReference(sNum)}
        </div>
        {/* type · tsk · status */}
        <div className="flex items-center gap-1.5">
          <DirBadge type={task.type} />
          <span
            className="text-[11px] font-bold uppercase tracking-wide"
            style={{ color: LANE_META[taskLane(task.type)].color }}
          >
            {getTaskTypeDisplay(task.type)}
          </span>
          <span className="font-mono text-[10.5px] text-zinc-400">
            TSK-{task.taskNumber}
          </span>
          <div className="flex-1" />
          {status && <ShipmentStatusBadge status={status} />}
        </div>

        {/* party */}
        <div className="mt-1.5 text-[13px] font-bold text-zinc-900">
          {task.contactName ?? "—"}
        </div>
        <div className="mt-1 text-[12px] text-zinc-700">
          {taskAddressLine(task)}
        </div>
        {taskRoutingPin(task) && (
          <div className="mt-1 font-mono text-[11px] text-zinc-500">
            Pin {taskRoutingPin(task)}
          </div>
        )}
        {unit && (
          <div className="mt-1 text-[12px] text-zinc-600">Unit {unit}</div>
        )}

        {/* shipment line */}
        {(sNum ?? ord ?? ship?.trackingNumber) && (
          <div className="mt-1 flex items-center gap-1.5 text-[11px]">
            <Package className="h-3.5 w-3.5 shrink-0 text-zinc-400" />

            {ship?.trackingNumber && (
              <span className="truncate font-mono text-zinc-400">
                {ship.trackingNumber}
              </span>
            )}
            {ord && (
              <span className="ml-auto shrink-0 font-mono text-zinc-400">
                ORD-{ord}
              </span>
            )}
          </div>
        )}

        {/* packages + care */}
        {(pkgs.length > 0 || fragile || cold) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-600">
            {pkgs.length > 0 && (
              <span className="tabular-nums">
                {pkgs.length} pkg · {weight} g
              </span>
            )}
            {fragile && (
              <span
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-white"
                style={{ background: "#0d9488" }}
              >
                <AlertTriangle className="h-3 w-3" /> Fragile
              </span>
            )}
            {cold && (
              <span
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-white"
                style={{ background: "#0d9488" }}
              >
                <Snowflake className="h-3 w-3" /> Cold
              </span>
            )}
          </div>
        )}

        {/* note */}
        {note && (
          <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-100 bg-amber-50 px-2 py-1.5 text-[11.5px] leading-snug text-amber-900">
            <StickyNote className="mt-0.5 h-3 w-3 shrink-0 text-amber-700" />
            {note}
          </div>
        )}

        {/* failure */}
        {task.failureReason && (
          <div className="mt-1.5 rounded border border-red-100 bg-red-50 px-2 py-1 text-[11px] text-red-700">
            {task.failureReason}
          </div>
        )}

        {held && (
          <Link
            to="/fulfillment/cancellations"
            className="mt-2 flex items-center gap-1.5 rounded-md bg-zinc-100 px-2 py-1.5 text-[11px] font-semibold text-zinc-700 hover:underline"
          >
            <PauseCircle className="h-3.5 w-3.5" /> Cancellation review — task
            actions on hold
          </Link>
        )}

        {/* proof */}
        {proof && proof.length > 0 && (
          <div className="mt-1.5 flex gap-1.5">
            {proof.slice(0, 3).map((m, i) => (
              <img
                key={i}
                src={m.url}
                alt="proof"
                className="h-10 w-10 rounded border border-zinc-200 object-cover"
              />
            ))}
          </div>
        )}
      </div>

      {inProgress && (
        <div className="flex border-t border-zinc-100 bg-zinc-50">
          <button
            onClick={onComplete}
            disabled={busy || held}
            title={held ? "Resolve the cancellation review first" : undefined}
            className="flex flex-1 items-center justify-center gap-1.5 border-r border-zinc-100 py-1.5 text-[11.5px] font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" /> Complete
          </button>
          <button
            onClick={onFail}
            disabled={busy || held}
            title={held ? "Resolve the cancellation review first" : undefined}
            className="flex flex-1 items-center justify-center gap-1.5 py-1.5 text-[11.5px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <XCircle className="h-3.5 w-3.5" /> Fail
          </button>
        </div>
      )}
      {done && (
        <div className="flex items-center gap-1.5 border-t border-green-100 bg-green-50 px-3 py-1.5 text-[11px] text-green-700">
          <Check className="h-3 w-3" /> Completed
          {task.completedAt &&
            " " +
              formatDistanceToNow(new Date(task.completedAt), {
                addSuffix: true,
              })}
        </div>
      )}
      {failed && (
        <div className="flex items-center gap-1.5 border-t border-red-100 bg-red-50 px-3 py-1.5 text-[11px] text-red-700">
          <XCircle className="h-3 w-3" /> Failed
        </div>
      )}
    </div>
  );
}

export function WaypointDetail({
  trip,
  stop,
  onClose,
  onOpenTrip,
}: WaypointDetailProps) {
  const queryClient = useQueryClient();
  const { data: waypoint, isLoading } = useWaypointById(trip.id, stop.id);
  const waypointQueries = useTripWaypoints([trip]);
  const totalStops = waypointQueries[0]?.data?.length ?? 0;
  const driver = trip.operator;
  const color = trip.color ?? "#6366f1";

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["waypoints", trip.id, stop.id],
    });
    void queryClient.invalidateQueries({ queryKey: ["trips", trip.id] });
  };
  const start = useStartWaypoint({
    onSuccess: () => {
      toast.success("Waypoint started");
      invalidate();
    },
    onError: () => toast.error("Couldn't start"),
  });
  const split = useSplitWaypoint();
  const [splitTaskId, setSplitTaskId] = useState("");
  const [sourceAnchorTaskId, setSourceAnchorTaskId] = useState("");
  const complete = useCompleteTask({
    onSuccess: () => {
      toast.success("Task completed");
      invalidate();
    },
    onError: () => toast.error("Couldn't complete"),
  });
  const fail = useFailTask({
    onSuccess: () => {
      toast.success("Task failed");
      invalidate();
    },
    onError: () => toast.error("Couldn't fail"),
  });

  const tasks = (waypoint?.tasks ?? stop.tasks) as RichTask[];
  const splitTask = tasks.find((task) => task.id === splitTaskId) ?? tasks[0];
  const sourceAnchor =
    tasks.find(
      (task) => task.id === sourceAnchorTaskId && task.id !== splitTask?.id
    ) ?? tasks.find((task) => task.id !== splitTask?.id);
  const canSplit =
    trip.status === "PLANNED" &&
    tasks.length >= 2 &&
    tasks.every(
      (task) => task.status === TaskStatus.PENDING && !task.startedAt
    ) &&
    !waypoint?.eta?.actualArrivalTime &&
    !waypoint?.eta?.actualDepartureTime;
  // Done = terminally resolved (completed, failed, cancelled) — a stop with
  // a failed/cancelled task plus a delivery must read 2/2, not 1/2.
  const completed = tasks.filter(isTaskDone).length;
  const hasPending = tasks.some((t) => t.status === TaskStatus.PENDING);
  const hasActionablePending = tasks.some(
    (task) =>
      task.status === TaskStatus.PENDING && !taskIsOnCancellationHold(task)
  );
  const orderCount = new Set(
    tasks.map((t) => t.shipment?.order?.orderNumber).filter(Boolean)
  ).size;
  const statusColor =
    stop.status === "done"
      ? "#16a34a"
      : stop.status === "active"
        ? color
        : "#71717a";

  return (
    <div className="flex h-full flex-col bg-white">
      {/* header */}
      <div className="flex items-center gap-2 border-b border-zinc-200 px-3.5 py-3">
        <span
          className="grid h-[26px] w-[26px] place-items-center rounded-lg font-mono text-[12px] font-bold tabular-nums text-white"
          style={{ background: statusColor }}
        >
          {stop.status === "done" ? (
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          ) : (
            stop.seq
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-bold text-zinc-900">
            {stop.addr}
          </div>
          <div className="text-[11px] text-zinc-500">
            Stop {stop.seq}
            {totalStops ? ` of ${totalStops}` : ""} ·{" "}
            <span className="font-mono">#{trip.tripNumber}</span>
          </div>
        </div>
        {hasPending && (
          <button
            onClick={() =>
              start.mutate({ tripId: trip.id, waypointId: stop.id })
            }
            disabled={start.isPending || !hasActionablePending}
            title={
              hasActionablePending
                ? undefined
                : "All pending tasks are in cancellation review"
            }
            className="inline-flex h-7 items-center gap-1 rounded-md bg-indigo-600 px-2 text-[11.5px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {start.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Play className="h-3 w-3" />
            )}
            {hasActionablePending ? "Start" : "On hold"}
          </button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-zinc-400 hover:bg-zinc-100"
          onClick={onClose}
        >
          <X className="size-[15px]" />
        </Button>
      </div>

      {/* window · eta · operator · done */}
      {trip.status === "PLANNED" && !trip.plannedRoute && (
        <p
          role="status"
          className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          Route needs refresh.
        </p>
      )}
      <div className="flex items-center gap-5 border-b border-zinc-100 px-3.5 py-3">
        {stop.window && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              Window
            </div>
            <div className="font-mono text-[15px] font-bold tabular-nums text-amber-600">
              {stop.window}
            </div>
          </div>
        )}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            ETA
          </div>
          <div className="font-mono text-[15px] font-bold tabular-nums text-zinc-900">
            {formatEta(waypoint?.eta?.estimatedArrivalTime) ?? stop.eta ?? "—"}
          </div>
        </div>
        {driver && (
          <div className="flex items-center gap-2">
            <div
              className="grid h-[26px] w-[26px] place-items-center rounded-full text-[10px] font-bold text-white"
              style={{ background: color }}
            >
              {getOperatorInitials(driver)}
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                Operator
              </div>
              <div className="text-[12px] font-semibold text-zinc-800">
                {getOperatorDisplayName(driver)}
              </div>
            </div>
          </div>
        )}
        <div className="ml-auto text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            Done
          </div>
          <div className="text-[13px] font-bold tabular-nums text-zinc-900">
            {completed}/{tasks.length}
          </div>
        </div>
      </div>

      {/* tasks */}
      <div className="flex-1 overflow-y-auto bg-zinc-50/40 px-3 py-3">
        {canSplit && waypoint && (
          <details className="mb-3 rounded-md border bg-white p-2 text-xs">
            <summary className="cursor-pointer font-semibold">
              Move Task to separate stop
            </summary>
            <div className="mt-2 space-y-2">
              <div className="space-y-1">
                <span>Task to move</span>
                <Select
                  value={splitTask?.id ?? ""}
                  onValueChange={(value) => {
                    setSplitTaskId(value);
                    setSourceAnchorTaskId("");
                  }}
                >
                  <SelectTrigger aria-label="Task to move" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.map((task) => (
                      <SelectItem key={task.id} value={task.id}>
                        TSK-{task.taskNumber} ·{" "}
                        {shipmentReference(task.shipment?.shipmentNumber)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span>Remaining stop pin</span>
                <Select
                  value={sourceAnchor?.id ?? ""}
                  onValueChange={setSourceAnchorTaskId}
                >
                  <SelectTrigger
                    aria-label="Remaining stop pin"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks
                      .filter(
                        (task) =>
                          task.id !== splitTask?.id && taskRoutingPin(task)
                      )
                      .map((task) => (
                        <SelectItem key={task.id} value={task.id}>
                          TSK-{task.taskNumber} · {taskRoutingPin(task)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <p>Current pin: {waypoint.locationKey}</p>
              <p>
                Remaining stop pin:{" "}
                {sourceAnchor ? taskRoutingPin(sourceAnchor) : "Unavailable"}
              </p>
              <p>
                Separate stop pin:{" "}
                {splitTask ? taskRoutingPin(splitTask) : "Unavailable"}
              </p>
              <Button
                type="button"
                disabled={
                  split.isPending ||
                  !sourceAnchor ||
                  !splitTask ||
                  !taskRoutingPin(splitTask)
                }
                onClick={async () => {
                  if (!splitTask || !sourceAnchor) return;
                  try {
                    await split.mutateAsync({
                      tripId: trip.id,
                      waypointId: waypoint.id,
                      taskId: splitTask.id,
                      sourceTaskIds: tasks.map((task) => task.id),
                      sourceAnchorTaskId: sourceAnchor.id,
                      expectedSourcePin: waypoint.locationKey,
                      expectedMovedPin: taskRoutingPin(splitTask) ?? "",
                      expectedRemainingPin: taskRoutingPin(sourceAnchor) ?? "",
                    });
                    toast.success(
                      "Task moved to a separate stop. Route refresh is pending."
                    );
                    invalidate();
                  } catch {
                    toast.error(
                      "Could not split stop. Review the Tasks and pins again."
                    );
                  }
                }}
              >
                Move Task to separate stop
              </Button>
            </div>
          </details>
        )}
        <div className="mb-2 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
          {tasks.length} tasks · {orderCount || 1} order
          {orderCount > 1 ? "s" : ""}
        </div>
        {isLoading && tasks.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
        ) : (
          <div className="space-y-2.5">
            {tasks.map((t) => (
              <TaskCard
                key={t.id}
                task={t}
                busy={complete.isPending || fail.isPending}
                onComplete={() => complete.mutate({ taskId: t.id })}
                onFail={() =>
                  fail.mutate({
                    taskId: t.id,
                    reason: "Marked failed from dispatch",
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* footer */}
      <div className="border-t border-zinc-200 px-3.5 py-3">
        <button
          onClick={() => onOpenTrip(trip)}
          className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200 text-[12.5px] font-semibold text-zinc-700 hover:bg-zinc-50"
        >
          <Maximize2 className="h-3.5 w-3.5" /> Open trip
        </button>
      </div>
    </div>
  );
}
